import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { LeftArrowWhite } from "../../assets/images/svg/LeftArrow";
import { CameraSorrell, CameraWhite } from "../../assets/images/svg/Camera";
import { UploadSvg } from "../../assets/images/svg/Upload";
import { Upload } from "antd";
import { observer } from "mobx-react";
import { applicationStore } from "src/store/ApplicationStore";
import ProcessingId from "../processing-id";
import { toast } from "react-toastify";
import { detect } from "src/utils/detect";
import * as tf from "@tensorflow/tfjs";
import LoadingComponent from "src/components/ui/LoadingComponent";
import { IdentificationTypes } from "src/enums/IdentificationTypes";
import logger from "src/utils/logger";
import { workflowStepIterator } from "src/services/workflowStepIterator";
import { NodeTypes } from "src/enums/NodeTypes";
import { imageStorage } from "src/utils/imageStorage";
import Preloader from "src/components/ui/Preloader";
import FilePreviewModal from "src/components/FilePreviewModal";
import { useTranslation } from "react-i18next";
import {
  COUNTRIES_WITH_CUSTOM_MODEL,
  COUNTRY_MODEL_MAP,
  DEFAULT_COUNTRY_KEY,
} from "src/constants/countryConfig";
import { parseServiceErrorMessage } from "src/helpers/parseServiceError";
import { getVerificationDocHeaderKey } from "src/constants/verificationDocs";
import { MAX_FILE_SIZE_MB } from "src/constants/fileConfig";
import {
  buildFileReaderTrackingPayload,
  trackSmartlookEvent,
} from "src/utils/smartlook";

const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const SUPPORTED_IMAGE_TYPES_LABEL = ["JPEG", "JPG", "PNG"].join(", ");
const { Dragger } = Upload;

const Verification = observer(() => {
  const [file, setFile] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const docs_type = applicationStore.docsType;
  const headerKey = location?.state?.headerKey;
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [pendingImage, setPendingImage] = useState(null);
  const [isCameraHover, setIsCameraHover] = useState(false);
  const previewUrlRef = useRef("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const [fileList, setFileList] = useState([]);

  const [modelLoading, setModelLoading] = useState({
    loading: true,
    progress: 0,
  });
  const [model, setModel] = useState({
    net: null,
    inputShape: [1, 0, 0, 3],
  });
  const modelName = "yolov8n";
  const { t } = useTranslation();
  const inprogressServices = Array.isArray(applicationStore.workflowNextServiceResponse?.inprogress_services)
    ? applicationStore.workflowNextServiceResponse.inprogress_services
    : [];
  const isFakeFlaggedFlow = inprogressServices.some(
    (service) =>
      service?.fake === true
  );

  useEffect(() => {
    const modelType = `_${COUNTRY_MODEL_MAP[applicationStore.country] ||
      DEFAULT_COUNTRY_KEY}`;
    tf.ready().then(async () => {
      const yolov8 = await tf.loadGraphModel(
        `${window.location.origin}/${modelName}_web_model${modelType}/model.json`,
        {
          onProgress: (fractions) => {
            setModelLoading({ loading: true, progress: fractions });
          },
        }
      );

      const dummyInput = tf.ones(yolov8.inputs[0].shape);
      const warmupResults = yolov8.execute(dummyInput);

      setModelLoading({ loading: false, progress: 1 });
      setModel({
        net: yolov8,
        inputShape: yolov8.inputs[0].shape,
      });

      tf.dispose([warmupResults, dummyInput]);
    });
  }, []);

  useEffect(() => {
    if (isFakeFlaggedFlow) {
      navigate("/take-a-photo", { replace: true });
    }
  }, [isFakeFlaggedFlow, navigate]);

  const acceptAttribute = ACCEPTED_IMAGE_TYPES.join(",");

  const clearSelectedFile = () => {
    setPendingImage(null);
    setSelectedFileName("");
    setFile(null);
    setFileList([]);

    if (imageRef.current) {
      imageRef.current.src = "";
    }

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }
  };

  const resetVerificationRetryState = async () => {
    clearSelectedFile();
    applicationStore.setDocumentSurface({ front: false, back: false });
    applicationStore.setImg(null);
    applicationStore.setBackImg(null);

    try {
      await imageStorage.clearAll();
    } catch (storageError) {
      logger.error("Error clearing verification images from memory", storageError);
    }
  };

  const handleBeforeUpload = useCallback(
    async (selectedFile) => {
      if (!selectedFile) {
        return Upload.LIST_IGNORE;
      }

      const sizeInMb = selectedFile.size / (1024 * 1024);
      if (sizeInMb > MAX_FILE_SIZE_MB) {
        toast.warning(t("addressVerification.fileTooLarge", { size: MAX_FILE_SIZE_MB }));
        return Upload.LIST_IGNORE;
      }

      if (selectedFile.type && !ACCEPTED_IMAGE_TYPES.includes(selectedFile.type)) {
        toast.error(t("fileUpload.invalidType", { types: SUPPORTED_IMAGE_TYPES_LABEL }));
        return Upload.LIST_IGNORE;
      }

      setFile(selectedFile);
      setPendingImage(null);
      setSelectedFileName(selectedFile.name);

      const objectUrl = URL.createObjectURL(selectedFile);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      previewUrlRef.current = objectUrl;

      if (imageRef.current) {
        imageRef.current.src = objectUrl;
      }

      setFileList([{ uid: selectedFile.uid || selectedFile.name, name: selectedFile.name, status: "done" }]);

      return false;
    },
    [t]
  );

  const prepareSelectedFile = (selectedFile) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        if (typeof base64Data !== "string") {
          trackSmartlookEvent(
            "verification_file_reader_null_result",
            buildFileReaderTrackingPayload({
              selectedFile,
              reader,
              status: "null_result",
              page: "verification",
              surface,
              eventSource: "verification_upload",
            })
          );
          reject(new Error("Failed to read selected file as data URL"));
          return;
        }

        logger.log(base64Data);

        const originalSizeInKB = (base64Data.length * 3) / 4 / 1024;
        logger.log(`Original image size: ${originalSizeInKB.toFixed(2)} KB`);
        trackSmartlookEvent(
          "verification_file_reader_success",
          {
            ...buildFileReaderTrackingPayload({
              selectedFile,
              reader,
              status: "success",
              page: "verification",
              surface,
              eventSource: "verification_upload",
            }),
            base64_length: base64Data.length,
            original_size_kb: Number(originalSizeInKB.toFixed(2)),
          }
        );

        setPendingImage(base64Data);
        resolve(base64Data);
      };
      reader.onerror = (error) => {
        trackSmartlookEvent(
          "verification_file_reader_error",
          {
            ...buildFileReaderTrackingPayload({
              selectedFile,
              reader,
              status: "error",
              page: "verification",
              surface,
              eventSource: "verification_upload",
            }),
            error_message: error?.type || error?.message || null,
          }
        );
        reject(error);
      };
      reader.onabort = () => {
        trackSmartlookEvent(
          "verification_file_reader_abort",
          buildFileReaderTrackingPayload({
            selectedFile,
            reader,
            status: "abort",
            page: "verification",
            surface,
            eventSource: "verification_upload",
          })
        );
        reject(new Error("File reading was aborted"));
      };
      reader.readAsDataURL(selectedFile);
    });

  const handleRemoveFile = () => {
    clearSelectedFile();
    setIsPreviewOpen(false);
  };

  const handleSubmit = async (frontImageFile, backImageFile) => {
    if (!frontImageFile && !backImageFile) {
      return false;
    }

    setLoading(true);

    const workflowResponse = applicationStore.workflowNextServiceResponse || {};
    const selectedCountryCode =
      applicationStore.country ||
      workflowResponse.country ||
      "";
    const normalizedCountry = selectedCountryCode
      ? selectedCountryCode.toUpperCase()
      : "";

    const requiresBothSides =
      applicationStore.docsType === IdentificationTypes.IDENTITY_CARD ||
      applicationStore.docsType === IdentificationTypes.RESIDENCE_PERMIT ||
      applicationStore.docsType === IdentificationTypes.DRIVER_LICENSE

    let formObject = {
      docs_type,
    };

    if (normalizedCountry) {
      formObject.country = normalizedCountry;
    }

    if (frontImageFile) {
      formObject.id_frontimage = frontImageFile;
      await imageStorage.saveImage("img", frontImageFile);
    }
    if (backImageFile) {
      if (requiresBothSides) {
        formObject.id_frontimage = await imageStorage.getImage("img");
      }
      formObject.id_backimage = backImageFile;
      await imageStorage.saveImage("backImg", backImageFile);
    }

    try {
      if (
        requiresBothSides &&
        applicationStore.documentSurface.front &&
        !applicationStore.documentSurface.back &&
        surface === "back"
      ) {
        const response = await workflowStepIterator({
          access_token: applicationStore.access_token,
          node_id: applicationStore.workflowNextServiceResponse.node_id,
          body: {
            ...formObject,
          },
          action_index: applicationStore.workflowNextServiceResponse.index,
          constextOfStep: {
            step: NodeTypes.VERIFICATION,
            retry: workflowResponse.retry,
          },
        });
        if (response.data?.service_results[0].status !== 200) {
          const rawMessage = response.data?.service_results[0]?.message;
          const parsedMessage = parseServiceErrorMessage(rawMessage);
          throw new Error(parsedMessage);
        }
        applicationStore.setWorkflowNextServiceResponse(response.data);
      } else if (!requiresBothSides) {
        const response = await workflowStepIterator({
          access_token: applicationStore.access_token,
          node_id: applicationStore.workflowNextServiceResponse.node_id,
          body: {
            ...formObject,
          },
          action_index: applicationStore.workflowNextServiceResponse.index,
          constextOfStep: {
            step: NodeTypes.VERIFICATION,
            retry: workflowResponse.retry,
          },
        });
        if (response.data?.service_results[0].status !== 200) {
          const rawMessage = response.data?.service_results[0]?.message;
          const parsedMessage = parseServiceErrorMessage(rawMessage);
          throw new Error(parsedMessage);
        }
        applicationStore.setWorkflowNextServiceResponse(response.data);
      }

      if (!applicationStore.documentSurface.front && surface === "front") {
        applicationStore.setDocumentSurface({
          ...applicationStore.documentSurface,
          front: true,
        });
        clearSelectedFile();
        if (
          applicationStore.documentSurface.front &&
          applicationStore.documentSurface.back
        ) {
          navigate("/processing-id", { replace: true });
          return true;
        }
        navigate("/verification", { replace: true });
        return true;
      }

      if (
        applicationStore.documentSurface.front &&
        !applicationStore.documentSurface.back &&
        surface === "back"
      ) {
        applicationStore.setDocumentSurface({
          ...applicationStore.documentSurface,
          back: true,
        });
        clearSelectedFile();
        navigate("/processing-id", { replace: true });
        return true;
      }

      clearSelectedFile();
      return true;
    } catch (error) {
      logger.error(error);

      if (requiresBothSides && surface === "back") {
        await resetVerificationRetryState();
        navigate("/verification", { replace: true });
      }

      toast.error(error?.message || t("verification.uploadFailed"));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleOnLoad = async () => {
    if (!file) {
      return;
    }

    setIsUploading(true);

    try {
      if (!hasCountryModel && isBackSurface) {
        await prepareSelectedFile(file);
        return;
      }

      const imageDetectResult = await detect(
        imageRef.current,
        model,
        canvasRef.current,
        { process: "uploadPhoto", country: applicationStore.country }
      );
      logger.log(imageDetectResult);

      if (imageDetectResult === undefined || imageDetectResult === null) {
        toast.warning(t("verification.documentNotValid"));
        clearSelectedFile();
        return;
      }

      if (imageDetectResult.klass === "not_valid") {
        toast.warning(t("verification.documentNotValid"));
        clearSelectedFile();
        return;
      }

      let countryName;
      if (applicationStore.country === "gb") {
        countryName = "uk";
      } else {
        countryName = applicationStore.country;
      }

      const expectedModelName = COUNTRIES_WITH_CUSTOM_MODEL.includes(
        applicationStore.country
      )
        ? `${countryName}_${applicationStore.modelCotrolName}${isBackSurface ? "_back" : ""}`
        : applicationStore.modelCotrolName;

      if (imageDetectResult.klass !== expectedModelName) {
        toast.warning(t("verification.documentDoesNotMatch"));
        clearSelectedFile();
        return;
      }

      await prepareSelectedFile(file);
    } catch (error) {
      logger.error("Error validating document image", error);
      toast.error(t("verification.uploadFailed"));
      clearSelectedFile();
    } finally {
      setIsUploading(false);
    }
  };
  const hasCountryModel = !!COUNTRY_MODEL_MAP[applicationStore.country];
  const surface = !applicationStore.documentSurface.front ? "front" : "back";
  const isBackSurface = surface === "back";
  const isContinueDisabled = !pendingImage || isUploading || loading;
  const isUploadDisabled = Boolean(pendingImage) || isUploading || loading;
  const handleContinue = async () => {
    if (!pendingImage || isUploading || loading) {
      return;
    }

    await handleSubmit(
      isBackSurface ? null : pendingImage,
      isBackSurface ? pendingImage : null
    );
  };
  const currentDocType = docs_type || applicationStore.verificationSteps.activeStep;
  const docHeaderKey = headerKey || getVerificationDocHeaderKey(currentDocType);
  const docLabel = docHeaderKey ? t(docHeaderKey) : "";

  return (
    <>
      {modelLoading.loading ? (
        <div className="custom-container">
          <LoadingComponent />
        </div>
      ) : (
        <>
          {loading ? (
            <ProcessingId />
          ) : (
            <div className="custom-container">
              <div className="flex flex-col h-full justify-between">
                <div>
                  <div className="text-left">
                    <Button
                      variant="primaryBack"
                      onClick={() => {
                        navigate("/next", { replace: true });
                      }}
                    >
                      <LeftArrowWhite />
                    </Button>
                  </div>

                  <div className="grid gap-4 mt-10">
                    <div className="text-h5 font-bold text-flow-heading">
                      {t('verification.header', { side: isBackSurface ? t('verification.side.back') : t('verification.side.front') })}
                    </div>
                    <div className="text-body text-Gray900">
                      {t('verification.instructions', {
                        andBack:
                          currentDocType === IdentificationTypes.IDENTITY_CARD ||
                            currentDocType === IdentificationTypes.RESIDENCE_PERMIT ||
                            currentDocType === IdentificationTypes.DRIVER_LICENSE
                            ? t('verification.andBack')
                            : '',
                        doc: docLabel || applicationStore.verificationSteps.activeStep,
                      })}
                    </div>
                  </div>

                  <div className="mt-6">
                    <Dragger
                      name="document"
                      multiple={false}
                      beforeUpload={handleBeforeUpload}
                      fileList={fileList}
                      showUploadList={false}
                      disabled={isUploadDisabled}
                      accept={acceptAttribute}
                      className={`flow-upload-dragger !rounded-[24px] !py-8 ${isUploadDisabled ? "cursor-not-allowed" : ""}`}
                      style={
                        isUploadDisabled
                          ? { borderStyle: "none" }
                          : {
                              border: "1px dashed var(--flow-border)",
                              backgroundColor: "var(--flow-surface)",
                            }
                      }
                    >
                      {isUploading ? (
                        <div className="h-[180px] flex items-center justify-center">
                          <Preloader variant="sorrell" />
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-4">
                          <UploadSvg />
                          <div className="text-center">
                            <p className="text-flow-heading text-xs">
                              {t('fileUpload.dragSubtitle')}{" "}
                              <span className="flow-upload-browse">
                                {t('fileUpload.browse')}
                              </span>
                            </p>
                            <p className="text-xs text-Gray700 mt-2">
                              {t('fileUpload.fileLimit', { size: MAX_FILE_SIZE_MB })}
                              <br />
                              {t('fileUpload.supportedTypes', { types: SUPPORTED_IMAGE_TYPES_LABEL })}
                            </p>
                          </div>
                        </div>
                      )}
                    </Dragger>
                  </div>

                  {selectedFileName && (
                    <div className="flow-upload-file-card mt-4 flex items-center justify-between rounded-lg border border-SorrellBrown200 px-4 py-3 gap-3">
                      <Button
                        type="text"
                        htmlType="button"
                        className="flow-upload-preview-button flex items-center gap-3 text-left flex-1 h-auto p-0"
                        onClick={() => setIsPreviewOpen(true)}
                      >
                        <div className="flow-upload-preview-thumbnail w-12 h-12 rounded-xl overflow-hidden bg-Gray100 flex items-center justify-center">
                          {pendingImage || previewUrlRef.current ? (
                            <img
                              src={pendingImage || previewUrlRef.current}
                              alt={selectedFileName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <UploadSvg />
                          )}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-Gray900 truncate max-w-40" title={selectedFileName}>
                            {selectedFileName}
                          </span>
                          <span className="text-xs flow-upload-inline-action">
                            {t('actions.preview')}
                          </span>
                        </div>
                      </Button>
                      <Button
                        type="link"
                        htmlType="button"
                        size="small"
                        onClick={handleRemoveFile}
                        className="text-xs flow-upload-inline-action px-0"
                      >
                        {t('fileUpload.remove')}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid gap-2 pb-16">
                  <Button onClick={handleContinue} disabled={isContinueDisabled}>
                    {t('actions.continue')}
                  </Button>

                  <Button
                    onClick={() => {
                      navigate("/take-a-photo", { replace: true });
                    }}
                    variant="secondaryWithIcon"
                    onMouseEnter={() => setIsCameraHover(true)}
                    onMouseLeave={() => setIsCameraHover(false)}
                  >
                    {isCameraHover ? <CameraWhite /> : <CameraSorrell />}
                    <span className="ms-2">{t('verification.takePhoto')}</span>
                  </Button>

                  {window?.top === window?.self && (
                    <Button
                      onClick={() => {
                        applicationStore.setContinueOnAnotherDevice("/verification");
                        navigate("/continue-on-another-device", { replace: true });
                      }}
                      variant="secondaryWithoutIcon"
                    >
                      {t('verification.continueAnotherDevice')}
                    </Button>
                  )}
                </div>
              </div>

              <div
                className="min-w-screen min-h-screen"
                style={{ display: "none" }}
              >
                <img
                  alt=""
                  src={previewUrlRef.current || undefined}
                  data-smartlook-block
                  ref={imageRef}
                  onLoad={handleOnLoad}
                />
                <canvas
                  width={model.inputShape[1]}
                  height={model.inputShape[2]}
                  ref={canvasRef}
                />
              </div>
              <FilePreviewModal
                isOpen={isPreviewOpen}
                onClose={() => setIsPreviewOpen(false)}
                fileContent={pendingImage || previewUrlRef.current}
                fileName={selectedFileName}
                fileType={file?.type}
                title={selectedFileName || t('actions.preview')}
              />
            </div>
          )}
        </>
      )}
    </>
  );
});

export default Verification;
